import React, { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { X, Check } from "lucide-react";
import { toast } from "sonner";

const NotificationPanel = ({ onClose, onUpdate }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axiosInstance.get("/notifications");
      setNotifications(response.data);
    } catch (error) {
      toast.error("Gagal memuat notifikasi");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axiosInstance.put(`/notifications/${id}/read`);
      fetchNotifications();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Gagal menandai sebagai dibaca");
    }
  };

  const markAllAsRead = async () => {
    try {
      await axiosInstance.put("/notifications/read-all");
      fetchNotifications();
      if (onUpdate) onUpdate();
      toast.success("Semua notifikasi ditandai sebagai dibaca");
    } catch (error) {
      toast.error("Gagal menandai semua notifikasi");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-end p-4" data-testid="notification-panel">
      <Card className="w-full max-w-md bg-[#1a1a1b] border-red-900/30 shadow-2xl mt-16 mr-4 max-h-[80vh] flex flex-col">
        <CardHeader className="border-b border-red-900/30">
          <div className="flex items-center justify-between">
            <CardTitle className="text-red-50">Notifikasi</CardTitle>
            <div className="flex gap-2">
              {notifications.some(n => !n.is_read) && (
                <Button
                  onClick={markAllAsRead}
                  variant="ghost"
                  size="sm"
                  className="text-sm text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                  data-testid="mark-all-read-button"
                >
                  <Check size={16} className="mr-1" />
                  Tandai semua
                </Button>
              )}
              <Button
                onClick={onClose}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
                data-testid="close-notification-button"
              >
                <X size={20} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-red-600 border-t-transparent"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8 text-gray-400">Tidak ada notifikasi</div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`p-4 rounded-lg border ${
                    notif.is_read
                      ? "bg-[#0f0f10] border-red-900/20"
                      : "bg-red-900/10 border-red-700/30"
                  }`}
                  data-testid={`notification-${notif.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-sm text-white">{notif.message}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(notif.created_at).toLocaleString('id-ID')}
                      </p>
                    </div>
                    {!notif.is_read && (
                      <Button
                        onClick={() => markAsRead(notif.id)}
                        variant="ghost"
                        size="sm"
                        className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                        data-testid={`mark-read-${notif.id}`}
                      >
                        <Check size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationPanel;